<div class="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto pr-1">
  @if (unavailableReason(); as reason) {
    <div class="rounded-2xl border border-dashed border-border bg-card/50 p-6 text-center text-sm text-muted-foreground">
      {{ reason }}
    </div>
  } @else if (loading()) {
    <!-- Big loading overlay — matches the calls grid pattern (text-lg + indeterminate bar). -->
    <div class="mx-auto mt-10 flex w-full max-w-xl flex-col items-center gap-4 px-8 text-muted-foreground">
      <div class="flex w-full flex-col items-center gap-2">
        <div class="text-lg font-semibold text-foreground">Loading call details…</div>
        <div class="text-sm">Resolving callnote and fetching contents.</div>
      </div>
      <div class="h-2 w-full overflow-hidden rounded-full bg-muted">
        <div class="h-full w-1/3 animate-indeterminate rounded-full bg-primary"></div>
      </div>
    </div>
  } @else if (error(); as err) {
    <hlm-alert tone="error">
      <hlm-icon name="lucideCircleAlert" size="1rem" class="mt-0.5" />
      <div class="flex flex-1 flex-col gap-2">
        <div>
          Failed to load call details (HTTP {{ err.status }}).
          @if (err.correlationId) {
            <span class="font-mono-feat text-sm text-muted-foreground">correlation-id: {{ err.correlationId }}</span>
          }
          @if (err.message) {
            <div class="text-sm text-muted-foreground">{{ err.message }}</div>
          }
        </div>
        <div>
          <button hlmBtn variant="outline" size="sm" type="button" (click)="reload()">
            <hlm-icon name="lucideRefreshCw" size="0.9rem" />
            Retry
          </button>
        </div>
      </div>
    </hlm-alert>
  } @else if (!note()) {
    <!--
      The backend returns HTTP 200 + empty body for some callnote IDs (commonly
      test calls / unsaved drafts). Surface the ID so operators can verify the
      lookup hit the right record, and explain the most likely cause so they
      don't suspect a frontend bug.
    -->
    <div class="rounded-2xl border border-dashed border-border bg-card/50 px-6 py-8 text-center">
      <div class="mx-auto flex max-w-md flex-col items-center gap-3">
        <span class="grid h-10 w-10 place-items-center rounded-full bg-muted text-muted-foreground">
          <hlm-icon name="lucideInbox" size="1.25rem" />
        </span>
        <div class="text-base font-semibold text-foreground">No callnote content</div>
        <p class="text-sm text-muted-foreground">
          The backend returned no content for this callnote.
          @if (callNoteId(); as cnId) {
            <span class="block pt-1 font-mono-feat text-sm">Callnote ID {{ cnId }}</span>
          }
        </p>
        <p class="text-sm text-muted-foreground/80">
          This is normal for test calls and certain unsaved drafts. Accounting / CDR Data,
          Call Notes, Billing and Transcript tabs still work — open one of those for the
          available data.
        </p>
      </div>
    </div>
  } @else if (note(); as n) {
    <!--
      OVERVIEW + CALLER + RECIPIENT — three columns on lg+, stack on smaller widths.
      Overview replaces the old full-width hero strip; same icon-badge + h2 + rows
      pattern as PS Info / Customer Information detail cards (SPARTAN_STYLE_GUIDE §6.3/§6.4).
    -->
    <div class="grid gap-5 lg:grid-cols-3">
      <section class="rounded-2xl border border-border bg-card p-5 shadow-sm">
        <header class="mb-4 flex flex-wrap items-center gap-2.5">
          <span class="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
            <hlm-icon name="lucideInfo" size="1rem" />
          </span>
          <h2 class="font-display text-lg font-semibold text-foreground">Overview</h2>
          <!-- State badges shift right of the title so the row keeps a single baseline. -->
          @if (n.urgent || !n.read || n.inTrash || n.caller?.vip || isBilled() === true) {
            <span class="ml-auto flex flex-wrap items-center gap-1.5">
              @if (n.urgent) {
                <hlm-badge variant="destructive" class="!px-2 !py-0.5">Urgent</hlm-badge>
              }
              @if (!n.read) {
                <hlm-badge variant="default" class="!px-2 !py-0.5">Unread</hlm-badge>
              }
              @if (n.inTrash) {
                <hlm-badge variant="secondary" class="!px-2 !py-0.5">In trash</hlm-badge>
              }
              @if (n.caller?.vip) {
                <hlm-badge variant="accent" class="!px-2 !py-0.5">VIP</hlm-badge>
              }
              @if (isBilled() === true) {
                <hlm-badge variant="success" class="!px-2 !py-0.5" hlmTooltip="At least one billing entry recorded for this call">Billed</hlm-badge>
              }
            </span>
          }
        </header>

        <dl class="flex flex-col divide-y divide-border/70">
          <div class="flex items-center justify-between gap-4 py-2.5">
            <dt class="cmt-eyebrow">Type</dt>
            <dd class="flex items-center gap-2 text-sm font-medium text-foreground">
              <span>{{ typeLabel() }}</span>
              @if (n.callNoteType) {
                <!--
                  Sub-type chip: same shape + same tone code as the calls-grid status
                  pill (callStatusTone). Combined label "Translation (RAW)" so the
                  human label is the headline and the API enum is right there for
                  support / Graylog correlation — no need to hover.
                -->
                <span
                  class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                  [class]="subTypeTone().pillClass"
                >
                  {{ subTypeLabel() }}&nbsp;<span class="font-mono-feat opacity-70">({{ subTypeRaw() }})</span>
                </span>
              }
            </dd>
          </div>
          <div class="flex items-start justify-between gap-4 py-2.5">
            <dt class="cmt-eyebrow pt-0.5">Agent</dt>
            <dd class="flex min-w-0 flex-col items-end gap-0.5 text-sm font-medium text-foreground">
              @if (agentBaseName(); as name) {
                <span class="flex flex-wrap items-baseline justify-end gap-x-1.5 gap-y-0.5">
                  <span>{{ name }}</span>
                  @if (agentLoginAccent(); as login) {
                    <span class="font-mono-feat text-xs text-muted-foreground">{{ login }}</span>
                  }
                  @if (agentIdAccent(); as id) {
                    <span class="font-mono-feat text-xs text-muted-foreground"
                          title="Employee ID"
                          data-testid="call-details-agent-id-chip">{{ id }}</span>
                  }
                </span>
                @if (agentMetaLine(); as meta) {
                  <span class="text-sm font-normal text-muted-foreground">{{ meta }}</span>
                }
              } @else {
                <span>—</span>
              }
            </dd>
          </div>
          <div class="flex items-center justify-between gap-4 py-2.5">
            <dt class="cmt-eyebrow">Started</dt>
            <dd class="font-mono-feat text-sm text-foreground">{{ formatDate(n.created) }}</dd>
          </div>
          @if (n.lastChange && n.lastChange !== n.created) {
            <div class="flex items-center justify-between gap-4 py-2.5">
              <dt class="cmt-eyebrow">Last change</dt>
              <dd class="font-mono-feat text-sm text-foreground">{{ formatDate(n.lastChange) }}</dd>
            </div>
          }
          @if (hasDurations() && durations(); as d) {
            <div class="flex items-center justify-between gap-4 py-2.5">
              <dt class="cmt-eyebrow">Call Duration</dt>
              <dd class="flex flex-wrap items-baseline justify-end gap-2 font-mono-feat text-sm text-foreground">
                <span>{{ formatSeconds(d.callDurationInSeconds) }}</span>
                <!-- EB-3813: for vermittelt calls the Call Duration covers the entire CDR span
                     (incl. post-transfer caller ↔ mediated party). Surface that here so the
                     number doesn't look inconsistent with Agent Time / old CMT "Bearb.dauer". -->
                @if (isTransferred()) {
                  <span
                    class="font-sans text-sm normal-case tracking-normal text-foreground"
                    title="Includes time after transfer (caller ↔ mediated party). Agent talk time is in Agent Time."
                    data-testid="call-duration-transfer-hint">
                    ↗ incl. transfer
                  </span>
                }
              </dd>
            </div>
            <div class="flex items-center justify-between gap-4 py-2.5">
              <dt class="cmt-eyebrow">Agent Time</dt>
              <dd class="font-mono-feat text-sm text-foreground">{{ formatSeconds(d.processDurationInSeconds) }}</dd>
            </div>
            <div class="flex items-center justify-between gap-4 py-2.5">
              <dt class="cmt-eyebrow">Wrap-up</dt>
              <dd
                class="font-mono-feat text-sm text-foreground"
                [attr.title]="
                  (d.postProcessDurationInSeconds ?? 0) < 0
                    ? 'Not applicable for transferred calls'
                    : null
                ">{{ formatSeconds(d.postProcessDurationInSeconds) }}</dd>
            </div>
            <div class="flex items-center justify-between gap-4 py-2.5">
              <dt class="cmt-eyebrow">Ring (cust/agent)</dt>
              <dd class="font-mono-feat text-sm text-foreground">
                {{ formatSeconds(d.customerConnectSeconds) }}
                <span class="text-muted-foreground">/</span>
                {{ formatSeconds(d.agentConnectSeconds) }}
              </dd>
            </div>
          }
        </dl>
      </section>

      <section class="rounded-2xl border border-border bg-card p-5 shadow-sm">
        <header class="mb-4 flex items-center gap-2.5">
          <span class="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
            <hlm-icon name="lucideUser" size="1rem" />
          </span>
          <h2 class="font-display text-lg font-semibold text-foreground">Caller</h2>
        </header>

        @if (hasCallerInfo()) {
          <dl class="flex flex-col divide-y divide-border/70">
            <div class="flex items-center justify-between gap-4 py-2.5">
              <dt class="cmt-eyebrow">Name</dt>
              <dd class="text-sm font-medium text-foreground">{{ callerName() }}</dd>
            </div>
            @if (n.caller?.company) {
              <div class="flex items-center justify-between gap-4 py-2.5">
                <dt class="cmt-eyebrow">Company</dt>
                <dd class="text-sm font-medium text-foreground">{{ n.caller?.company }}</dd>
              </div>
            }
            <div class="flex items-center justify-between gap-4 py-2.5">
              <dt class="cmt-eyebrow">Phone</dt>
              <dd class="font-mono-feat text-sm text-foreground">{{ n.caller?.phone || '—' }}</dd>
            </div>
            <div class="flex items-center justify-between gap-4 py-2.5">
              <dt class="cmt-eyebrow">CLIP</dt>
              <dd class="font-mono-feat text-sm text-foreground">{{ n.clipTelephone || '—' }}</dd>
            </div>
            <div class="flex items-center justify-between gap-4 py-2.5">
              <dt class="cmt-eyebrow">Email</dt>
              <dd class="text-sm font-medium text-foreground">{{ n.caller?.email || '—' }}</dd>
            </div>
            <div class="flex items-center justify-between gap-4 py-2.5">
              <dt class="cmt-eyebrow">Contact</dt>
              <dd class="text-sm font-medium text-foreground">{{ n.contactPerson || '—' }}</dd>
            </div>
          </dl>
        } @else {
          <p class="text-sm italic text-muted-foreground">No caller info transmitted (CLIP withheld).</p>
        }
      </section>

      <section class="rounded-2xl border border-border bg-card p-5 shadow-sm">
        <header class="mb-4 flex items-center gap-2.5">
          <span class="grid h-8 w-8 place-items-center rounded-lg bg-accent/20 text-accent-foreground">
            <hlm-icon name="lucidePhoneOutgoing" size="1rem" />
          </span>
          <h2 class="font-display text-lg font-semibold text-foreground">Recipient</h2>
        </header>

        <dl class="flex flex-col divide-y divide-border/70">
          <div class="flex items-center justify-between gap-4 py-2.5">
            <dt class="cmt-eyebrow">Number</dt>
            <dd
              class="font-mono-feat text-sm text-foreground"
              [attr.title]="'Area code ' + (n.callee?.vorwahl || '—') + ' · Prefix ' + (n.callee?.prefix || '—') + ' · Suffix ' + (n.callee?.suffix || '—')"
            >
              {{ calleeFormatted() }}
            </dd>
          </div>
          <!--
            Data rows stay pure data + copy. Jump-to-entity actions live in a
            single action bar at the bottom of the card (see below) so the
            value column reads as one tidy data block instead of a mix of
            value/button per row. The action bar keeps the card visually
            balanced (otherwise the bottom of Recipient looks empty next to
            the data-heavy Caller / Overview cards).
          -->
          <div class="flex items-center justify-between gap-4 py-2.5">
            <dt class="cmt-eyebrow">Phone Service</dt>
            <dd class="text-sm font-medium text-foreground">
              {{ n.phoneServiceName || '—' }}
              @if (n.phoneServiceId !== null) {
                <span class="ml-1 inline-flex items-center gap-1">
                  <span class="font-mono-feat text-muted-foreground">(PS {{ n.phoneServiceId }})</span>
                  <button
                    type="button"
                    class="cmt-copy-btn"
                    (click)="copyValue(n.phoneServiceId, 'PS ID')"
                    hlmTooltip="Copy Phone Service ID"
                    [attr.aria-label]="'Copy Phone Service ID ' + n.phoneServiceId"
                  >
                    <hlm-icon name="lucideCopy" size="0.75rem" />
                  </button>
                </span>
              }
            </dd>
          </div>
          <div class="flex items-center justify-between gap-4 py-2.5">
            <dt class="cmt-eyebrow">Customer</dt>
            <dd class="text-sm font-medium text-foreground">{{ n.customerCompany || '—' }}</dd>
          </div>
          <div class="flex items-center justify-between gap-4 py-2.5">
            <dt class="cmt-eyebrow">User Account</dt>
            <dd class="font-mono-feat text-sm text-foreground">
              @if (n.userAccountId !== null) {
                <span class="inline-flex items-center gap-1">
                  {{ n.userAccountId }}
                  <button
                    type="button"
                    class="cmt-copy-btn"
                    (click)="copyValue(n.userAccountId, 'UA ID')"
                    hlmTooltip="Copy User Account ID"
                    [attr.aria-label]="'Copy User Account ID ' + n.userAccountId"
                  >
                    <hlm-icon name="lucideCopy" size="0.75rem" />
                  </button>
                </span>
              } @else { — }
            </dd>
          </div>
        </dl>

        @if (canJump()) {
          <!--
            Jump action bar — sits at the bottom of the Recipient card. Lifted
            visually with a tinted bg, a "Go to" eyebrow header and primary
            buttons so it reads as a deliberate "next-step" panel rather than
            a row of secondary controls. The icon + entity noun communicates
            navigation; the surface tint groups the three together as a unit.
          -->
          <div class="mt-4 flex flex-col gap-2 rounded-xl border border-primary/20 bg-primary/5 p-3">
            <span class="cmt-eyebrow text-primary">Go to</span>
            <div class="grid grid-cols-3 gap-2">
              @if (n.phoneServiceId !== null) {
                <button
                  hlmBtn variant="default" size="sm"
                  type="button"
                  (click)="openPhoneService(n.phoneServiceId)"
                  [attr.aria-label]="'Open phone service ' + n.phoneServiceId"
                >
                  <hlm-icon name="lucideExternalLink" size="0.9rem" />
                  Phone service
                </button>
              } @else {
                <span></span>
              }
              <button
                hlmBtn variant="default" size="sm"
                type="button"
                (click)="openCustomer()"
                aria-label="Open customer"
              >
                <hlm-icon name="lucideExternalLink" size="0.9rem" />
                Customer
              </button>
              @if (n.userAccountId !== null) {
                <button
                  hlmBtn variant="default" size="sm"
                  type="button"
                  (click)="openUserAccount(n.userAccountId)"
                  [attr.aria-label]="'Open user account ' + n.userAccountId"
                >
                  <hlm-icon name="lucideExternalLink" size="0.9rem" />
                  User account
                </button>
              } @else {
                <span></span>
              }
            </div>
          </div>
        }
      </section>
    </div>

    <!-- CONTENT — prose section. Boxed body matches the Greeting card pattern in PS Info. -->
    <section class="rounded-2xl border border-border bg-card p-5 shadow-sm">
      <header class="mb-4 flex items-center gap-2.5">
        <span class="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
          <hlm-icon name="lucideFileText" size="1rem" />
        </span>
        <h2 class="font-display text-lg font-semibold text-foreground">Content</h2>
        @if (n.labels && n.labels.length > 0) {
          <span class="ml-2 flex flex-wrap items-center gap-1.5">
            @for (l of n.labels; track l) {
              <hlm-badge variant="default" class="!px-2 !py-0.5">{{ l }}</hlm-badge>
            }
          </span>
        }
      </header>

      @if (n.callReason) {
        <div class="mb-3 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2">
          <span class="cmt-eyebrow text-primary">Call reason</span>
          <div class="mt-0.5 text-sm text-foreground">{{ n.callReason }}</div>
        </div>
      }

      @if (contentForDisplay(); as cd) {
        @if (cd.short) {
          <p class="mb-3 text-base font-medium text-foreground">{{ cd.short }}</p>
        }
        @if (cd.full) {
          <div class="rounded-lg border border-border/50 bg-muted/50 p-3 text-sm leading-relaxed text-foreground whitespace-pre-wrap break-words">{{ cd.full }}</div>
        } @else if (!cd.short) {
          <p class="text-sm italic text-muted-foreground">No content.</p>
        }
      }
    </section>

    <!--
      Technical Details (LEFT) + Custom Fields (RIGHT) — bottom 2-column row.
      Each card uses the standard divide-y row pattern inside since it now sits
      in a half-width column. min-w-0 + break-words on value cells guards against
      long string values (URLs, addresses, IDs) pushing the column apart.
    -->
    <div class="grid gap-5 lg:grid-cols-2">
      <!-- TECHNICAL DETAILS (left) — admin fields. Read state already lives as a hero
           badge; Color row hidden when value is NONE / null. -->
      <section class="rounded-2xl border border-border bg-card p-5 shadow-sm">
        <header class="mb-4 flex items-center gap-2.5">
          <span class="grid h-8 w-8 place-items-center rounded-lg bg-muted text-muted-foreground">
            <hlm-icon name="lucideSettings" size="1rem" />
          </span>
          <h2 class="font-display text-lg font-semibold text-foreground">Technical details</h2>
        </header>
        <dl class="flex flex-col divide-y divide-border/70">
          <div class="flex min-w-0 items-center justify-between gap-4 py-2.5">
            <dt class="cmt-eyebrow">Callnote ID</dt>
            <dd class="min-w-0 break-words text-right font-mono-feat text-sm text-foreground">
              @if (n.id !== null) {
                <span class="inline-flex items-center gap-1">
                  {{ n.id }}
                  <button
                    type="button"
                    class="cmt-copy-btn"
                    (click)="copyValue(n.id, 'Callnote ID')"
                    hlmTooltip="Copy Callnote ID"
                    [attr.aria-label]="'Copy Callnote ID ' + n.id"
                  >
                    <hlm-icon name="lucideCopy" size="0.75rem" />
                  </button>
                </span>
              } @else { — }
            </dd>
          </div>
          <div class="flex min-w-0 items-center justify-between gap-4 py-2.5">
            <dt class="cmt-eyebrow">Priority</dt>
            <dd class="min-w-0 break-words text-right text-sm font-medium text-foreground">{{ n.priority ?? '—' }}</dd>
          </div>
          <div class="flex min-w-0 items-center justify-between gap-4 py-2.5">
            <dt class="cmt-eyebrow">Folder</dt>
            <dd class="min-w-0 break-words text-right text-sm font-medium text-foreground">
              @if (n.folder) {
                <span [attr.title]="n.folder.folderName">{{ folderTypeLabel(n.folder.folderType) }}</span>
              } @else { — }
            </dd>
          </div>
          @if (hasColor()) {
            <div class="flex min-w-0 items-center justify-between gap-4 py-2.5">
              <dt class="cmt-eyebrow">Color</dt>
              <dd class="min-w-0 break-words text-right text-sm font-medium text-foreground">{{ colorLabel() }}</dd>
            </div>
          }
          <div class="flex min-w-0 items-center justify-between gap-4 py-2.5">
            <dt class="cmt-eyebrow">Attachments</dt>
            <dd class="min-w-0 break-words text-right text-sm font-medium text-foreground">{{ n.attachments?.length ?? 0 }}</dd>
          </div>
          @if (n.callcenterAgentSignature) {
            <!-- Long string: stack to give it room without crashing the layout. -->
            <div class="flex min-w-0 flex-col gap-1 py-2.5">
              <dt class="cmt-eyebrow">Agent signature</dt>
              <dd class="break-words text-sm font-medium text-foreground">{{ n.callcenterAgentSignature }}</dd>
            </div>
          }
        </dl>
      </section>

      <!-- CUSTOM FIELDS (right) — optional. Each item is stacked label/value with
           min-w-0 + break-words so long string values (URLs, addresses, notes) wrap
           inside the column. Empty section suppresses the card entirely. -->
      @if (n.formfields && n.formfields.length > 0) {
        <section class="rounded-2xl border border-border bg-card p-5 shadow-sm">
          <header class="mb-4 flex items-center gap-2.5">
            <span class="grid h-8 w-8 place-items-center rounded-lg bg-muted text-muted-foreground">
              <hlm-icon name="lucideClipboard" size="1rem" />
            </span>
            <h2 class="font-display text-lg font-semibold text-foreground">
              {{ n.formfields.length === 1 ? 'Custom field' : 'Custom fields' }}
            </h2>
          </header>
          <dl class="flex flex-col gap-3">
            @for (f of n.formfields; track f.id) {
              <div class="flex min-w-0 flex-col gap-1 rounded-lg border border-border/50 bg-muted/30 p-3">
                <dt class="cmt-eyebrow">
                  {{ f.name }} <span class="normal-case text-muted-foreground/80">({{ f.type }})</span>
                </dt>
                <dd class="break-words text-sm font-medium text-foreground">{{ f.value || '—' }}</dd>
              </div>
            }
          </dl>
        </section>
      }
    </div>
  }
</div>
